import { Col, Form, Row, Tag, Tooltip, Typography } from 'antd';
import classNames from 'classnames';
import { isEmpty, some } from 'lodash-es';
import { Fragment, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { normalizeString } from '../../common/utils/stringUtils';
import IconButton from '../../components/buttons/IconButton';
import { renderDataStateIndicator } from '../../components/data/dataStateHandlers';
import { extractValueFromEvent } from '../../components/forms/formHelpers';
import { useFormContext } from '../../components/forms/forms';
import { FormItemSearchFlat } from '../../components/forms/searchFormElements';
import { InnerContentScrollbars } from '../../components/layout/InnerContentScrollbars';
import { FAIcon } from '../../components/adapters/fontAwesomeAdapters';
import { InlineLink } from '../../components/typography';
import { cssVariables } from '../../styles/cssVariables';
import { pxToNumber } from '../../utils/cssUtils';
import { useReportsSharedStyles } from './reportsShared.styles';
import { useReportsSelectionSectionStyles } from './ReportsSelectionSection.styles';

function Search({ value, onChange, setClosed, placeholderId }) {
  return (
    <Form component={false}>
      <FormItemSearchFlat
        name="accountSearch"
        onCancel={() => {
          onChange('');
          setClosed();
        }}
        formItemComponentProps={{
          autoFocus: true,
          value,
          onChange: e => onChange(extractValueFromEvent(e)),
          prefixIconSize: null,
        }}
        placeholderId={placeholderId}
      />
    </Form>
  );
}

function Item({
  columns,
  item,
  index,
  getKey,
  fieldName,
  isItemDisabled,
  getDisabledReason,
}) {
  const { values, formInstance, forceUpdate } = useFormContext();
  const { styles } = useReportsSelectionSectionStyles();
  const active = some(values[fieldName], val => getKey(val) === getKey(item));
  const disabled = isItemDisabled ? isItemDisabled(item) : false;
  const disabledReason =
    disabled && getDisabledReason ? getDisabledReason(item) : '';

  const content = (
    <Row gutter={pxToNumber(cssVariables.spaceNorm)}>
      {columns.map(col => (
        <Col key={col.key} span={col.span}>
          {col.render(item)}
        </Col>
      ))}
    </Row>
  );

  return (
    <div
      className={classNames(
        styles.row,
        index % 2 === 0 ? styles.rowEven : styles.rowOdd,
        {
          Clickable: !disabled,
          [styles.rowDisabled]: disabled,
        }
      )}
      onClick={() => {
        if (disabled) return;
        const key = getKey(item);
        const oldVal = formInstance.getFieldValue(fieldName);
        formInstance.setFieldsValue({
          [fieldName]: active
            ? oldVal.filter(val => getKey(val) !== key)
            : [...oldVal, item],
        });
        forceUpdate();
      }}
    >
      {active && <div className={styles.rowActive} />}
      <div className={styles.rowContent}>
        <div
          className={classNames(styles.rowInner, {
            [styles.rowInnerDisabled]: disabled,
          })}
        >
          {content}
        </div>
        {disabled && disabledReason && (
          <Tooltip title={disabledReason}>
            <span className={styles.infoIcon}>
              <FAIcon icon="info-circle" />
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

const DEFAULT_SPLIT_INTO_GROUPS = items => [{ key: 'default', items }];

export function ReportsSelectionSection({
  index: sectionIndex,
  titleId,
  headerId,
  subtitleId,
  subtitleRightContent,
  searchPlaceholderId,
  items,
  loading,
  error,
  getSearchSourcesFromItem,
  splitIntoGroups = DEFAULT_SPLIT_INTO_GROUPS,
  fieldName,
  getKey,
  getLabel,
  columns,
  isItemDisabled,
  getDisabledReason,
}) {
  const [searchIsOpen, setSearchOpen] = useState();
  const [searchText, setSearchText] = useState('');
  const { values, formInstance, forceUpdate } = useFormContext();
  const { styles: sharedStyles } = useReportsSharedStyles();
  const { styles } = useReportsSelectionSectionStyles();
  const selected = values[fieldName];

  const indicator = renderDataStateIndicator({ error, loading });
  const filteredItems = items?.filter(item =>
    some(
      getSearchSourcesFromItem(item),
      fieldValue =>
        normalizeString(fieldValue).indexOf(normalizeString(searchText)) > -1
    )
  );

  const groups = splitIntoGroups(filteredItems);

  return (
    <div className={sharedStyles.reportsSectionContent}>
      <div
        className={classNames(
          sharedStyles.reportsSectionTitle,
          'no-margin-form-items'
        )}
      >
        <Typography.Title level={3}>
          {sectionIndex}. <FormattedMessage id={titleId} />
        </Typography.Title>
        <Row align="middle" gutter={pxToNumber(cssVariables.spaceSm)}>
          <Col>
            {searchIsOpen ? (
              <Search
                value={searchText}
                onChange={setSearchText}
                setClosed={() => setSearchOpen(false)}
                placeholderId={searchPlaceholderId}
              />
            ) : (
              <IconButton
                icon="search"
                className="icon-18"
                onClick={() => setSearchOpen(true)}
              />
            )}
          </Col>
          <Col>
            <InlineLink
              textId="buttons.clear"
              onClick={() => {
                formInstance.setFieldsValue({ [fieldName]: [] });
                forceUpdate();
              }}
            />
          </Col>
        </Row>
      </div>
      <div className={sharedStyles.selectionSection}>
        <div className={sharedStyles.selectionSectionTitle}>
          <FormattedMessage id={headerId} />
        </div>
        <div className={styles.subtitle}>
          <div className={styles.subtitleLeft}>
            {isEmpty(selected) ? (
              <FormattedMessage id={subtitleId} />
            ) : (
              <>
                <span className="label">
                  <FormattedMessage id="reports.selection.selected" />
                </span>
                {selected.map(val => (
                  <Tag
                    className={sharedStyles.selectionTag}
                    key={`report-selection-tag-${getKey(val)}`}
                  >
                    {getLabel(val)}
                  </Tag>
                ))}
              </>
            )}
          </div>
          <div className="no-margin-form-items">{subtitleRightContent}</div>
        </div>
        <div className={styles.header}>
          <Row gutter={pxToNumber(cssVariables.spaceNorm)}>
            {columns.map(col => (
              <Col key={col.key} span={col.span}>
                <FormattedMessage id={col.labelId} />
              </Col>
            ))}
          </Row>
        </div>
        <InnerContentScrollbars>
          <div className={sharedStyles.selectionSectionBody}>
            {indicator ||
              groups?.map(group => (
                <Fragment key={group.key}>
                  {group.labelId ? (
                    <div
                      className={classNames(
                        styles.groupLabel,
                        group.variant === 'partner' && styles.groupLabelPartner,
                        group.variant === 'nonPartner' &&
                          styles.groupLabelNonPartner
                      )}
                    >
                      <FormattedMessage id={group.labelId} />
                    </div>
                  ) : (
                    <div />
                  )}
                  {group.items.map((item, index) => (
                    <Item
                      key={`report-selection-item-${getKey(item)}`}
                      columns={columns}
                      item={item}
                      index={index}
                      getKey={getKey}
                      fieldName={fieldName}
                      isItemDisabled={isItemDisabled}
                      getDisabledReason={getDisabledReason}
                    />
                  ))}
                </Fragment>
              ))}
          </div>
        </InnerContentScrollbars>
      </div>
    </div>
  );
}
